import { Box, Button } from '@mui/material';
import Loader from 'components/Loader';
import { Procedure } from 'interfaces/procedure';
import React, { ComponentProps } from 'react';

import { ManifestWorkspaceSurface } from './CaseManifestControls';
import { CaseManifestPlanTable } from './CaseManifestPlanTable';
import { CaseManifestSourcePanel } from './CaseManifestSourcePanel';
import { ManifestErrors, ManifestWarnings } from './ManifestFeedback';
import NotEditableSlidesDataGrid from './NotEditableSlidesDataGrid';
import SlideDirectAddSearch from './SlideDirectAddSearch';

interface CaseManifestWorkspaceProps {
  activeSurface: ManifestWorkspaceSurface;
  editingPaused: boolean;
  loadingStatusText: string | null;
  planTableProps: Omit<ComponentProps<typeof CaseManifestPlanTable>, 'editingPaused'>;
  sourcePanelProps: ComponentProps<typeof CaseManifestSourcePanel>;
  directAddSearchProps: ComponentProps<typeof SlideDirectAddSearch>;
  notIngestedSlides: Procedure[];
  errorMessages: string[];
  warningMessages: string[];
  onResolveDuplicateRows?: () => void;
}

export const CaseManifestWorkspace = ({
  activeSurface,
  editingPaused,
  loadingStatusText,
  planTableProps,
  sourcePanelProps,
  directAddSearchProps,
  notIngestedSlides,
  errorMessages,
  warningMessages,
  onResolveDuplicateRows,
}: CaseManifestWorkspaceProps) => (
  <>
    {loadingStatusText && (
      <Box mb={1.5}>
        <Loader inline message={loadingStatusText} testId="case-manifest-loading-indicator" />
      </Box>
    )}
    {/* A disabled fieldset pauses every control inside it while a workbook import is resolving. */}
    <Box
      component="fieldset"
      data-testid="case-manifest-workspace"
      disabled={editingPaused}
      sx={{
        border: 0,
        m: 0,
        minWidth: 0,
        p: 0,
        ...(activeSurface === 'Manifest' && { display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }),
      }}
    >
      {activeSurface === 'Manifest' ? (
        <CaseManifestPlanTable {...planTableProps} editingPaused={editingPaused} />
      ) : (
        <Box my={1}>
          {activeSurface === 'Cross Lab Search' ? (
            <SlideDirectAddSearch {...directAddSearchProps} />
          ) : activeSurface === 'Not Ingested Slides' ? (
            <NotEditableSlidesDataGrid procedures={notIngestedSlides} />
          ) : activeSurface === 'Lab Lost and Found Slides' || activeSurface === 'Study Lost and Found Slides' ? (
            <CaseManifestSourcePanel {...sourcePanelProps} />
          ) : null}
        </Box>
      )}
    </Box>
    <ManifestErrors messages={errorMessages} />
    {onResolveDuplicateRows && (
      <Button color="inherit" onClick={onResolveDuplicateRows} disabled={editingPaused}>
        Resolve duplicate rows
      </Button>
    )}
    <ManifestWarnings messages={warningMessages} />
  </>
);
